"use client";

import React from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card, CardContent } from "@/components/ui/card";
import { Droplets, Shield, Sparkles, Wind, CircleCheck, Calendar, Compass, Package, PlaneTakeoff, Image } from "lucide-react";
const steps = [{
  id: "01",
  title: "Curated Discovery",
  description: "Our experts hand-select hidden gems and exclusive routes, ensuring your itinerary begins with inspiration and unique local insights.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1650028/img/ancient_landmark_discovery.png",
  icon: Compass
}, {
  id: "02",
  title: "Bespoke Planning",
  description: "We tailor every detail to your preferences, from luxury transport to private guides, crafting a seamless flow for your upcoming adventure.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1650028/img/voyage_planning.png",
  icon: Calendar
}, {
  id: "03",
  title: "Seamless Logistics",
  description: "From visa handling to priority boarding, we manage the complexities so you can focus entirely on the anticipation of your journey.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1650028/img/voyage_logistics.png",
  icon: Package
}, {
  id: "04",
  title: "The Grand Departure",
  description: "Step into a world of effortless exploration with 24/7 concierge support and meticulously timed transitions at every destination.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1650028/img/voyage_departure.png",
  icon: PlaneTakeoff
}, {
  id: "05",
  title: "Lasting Memories",
  description: "Return with more than just photos; our post-trip follow-up ensures your experiences are preserved and your next dream is already taking shape.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1650028/img/voyage_memories.png",
  icon: Image
}];
function TimelineStep({
  step,
  index
}: {
  step: (typeof steps)[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.3
  });
  const isEven = index % 2 === 0;
  const Icon = step.icon;
  return <div data-section-id="224836" ref={ref} data-index={index} className={`relative flex flex-col md:flex-row items-center justify-between w-full mb-16 md:mb-24 last:mb-0 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
      {}
      <div className="absolute left-8 md:left-1/2 top-0 md:top-1/2 -translate-x-1/2 md:-translate-y-1/2 flex flex-col items-center justify-center z-20">
        <div className={`w-6 h-6 rounded-full border-4 border-background flex items-center justify-center transition-colors duration-700 delay-300 ${isVisible ? "bg-primary shadow-[0_0_20px_hsl(var(--primary))]" : "bg-muted"}`}>
          <div className={`w-2 h-2 rounded-full ${isVisible ? "bg-background" : "bg-transparent"}`} />
        </div>
      </div>

      {}
      <div className={`absolute left-8 top-6 bottom-[-4rem] md:hidden w-1 -translate-x-1/2 transition-all duration-1000 origin-top ${isVisible ? "bg-primary scale-y-100" : "bg-border scale-y-0"}`} />

      {}
      <div className={`w-full pl-20 md:pl-0 md:w-[45%] flex ${isEven ? "md:justify-end md:pr-12" : "md:justify-start md:pl-12 md:order-last"}`}>
        <Card className="relative overflow-hidden bg-card/20 backdrop-blur-2xl border-border/30 shadow-2xl group hover:border-primary/40 transition-colors duration-500 w-full">
          {}
          <div className="absolute -right-4 -top-8 text-[8rem] font-default font-black text-primary/5 select-none pointer-events-none transition-transform duration-700 group-hover:scale-110">
            {step.id}
          </div>

          <CardContent className="p-6 md:p-8 relative z-10 flex flex-col gap-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-primary/20 text-primary shadow-[0_0_15px_rgba(var(--primary),0.2)]">
                <Icon className="w-6 h-6" />
              </div>
              <h3 className="text-2xl font-default font-bold text-foreground tracking-tight">
                {step.title}
              </h3>
            </div>

            <p className="text-muted-foreground font-default leading-relaxed text-base">
              {step.description}
            </p>

            <div className="relative h-48 w-full rounded-lg overflow-hidden mt-2">
              <div className="absolute inset-0 bg-gradient-to-t from-background/90 to-transparent z-10" />
              <img src={step.image} alt={step.title} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110 grayscale-[30%] group-hover:grayscale-0" />
            </div>
          </CardContent>
        </Card>
      </div>

      {}
      <div className="hidden md:block md:w-[45%]" />
    </div>;
}
export default function TheEcoShineProcess() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="from-dream-to-departure" className="dark py-24 md:py-32 bg-background text-foreground relative overflow-hidden">
      {}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-primary/10 rounded-full blur-[140px] pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-20 md:mb-32 transition-all duration-1000 ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <h2 className="text-4xl md:text-6xl font-serif font-bold text-foreground mb-6 tracking-tighter">
            From <span className="text-primary italic">Dream</span> to Departure
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-default max-w-2xl mx-auto">
            A cinematic journey through our meticulous planning process, designed to transform your travel aspirations into a flawlessly executed reality.
          </p>
        </div>

        <div className="relative max-w-5xl mx-auto">
          {}
          <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-border to-transparent -translate-x-1/2" />

          {}
          <div className="relative flex flex-col">
            {steps.map((step, index) => <TimelineStep key={step.id} step={step} index={index} />)}
          </div>
        </div>
      </div>
    </section>;
}